import { calmLogger } from '@/utils/calmLogger';

/**
 * Detects post-deploy stale-chunk failures and recovers with a single full reload.
 *
 * A deploy rewrites the hashed filenames under `/_next/static/chunks/*`. A client that
 * was loaded against the previous deploy still references the old hashes, so when it
 * lazily requests a chunk (route navigation, `next/dynamic`, `import()`) the file 404s
 * and webpack/Turbopack throws a `ChunkLoadError`. A full `location.reload()` re-fetches
 * the current HTML + asset manifest (served by the new pod, or by an S3 stale-asset
 * fallback), which resolves the mismatch.
 *
 * Loop guard: the reload is keyed to the build version (`BUILD_ID`, inlined per-deploy by
 * `next.config.ts`). We reload at most once per build id, so a *genuinely* missing chunk
 * (a broken deploy, not a stale client) reloads once, sees the same failure on the same
 * build, and then stops instead of looping forever. A later deploy ships a new `BUILD_ID`
 * and is allowed to recover again.
 *
 * That sentinel lives in `sessionStorage` and is keyed to `BUILD_ID`, so it fails open
 * (toward reloading) whenever either can't be trusted: storage denied (privacy mode,
 * storage policy) throws on every read/write, and a client bouncing between pods on
 * different builds during a rolling deploy never sees a matching sentinel. A `_ccr`/`_ccrts`
 * query-param counter backs it up — it survives `location.reload()` in the URL itself, so it
 * caps total reloads even when storage and build identity both can't be trusted.
 *
 * The trigger is also scoped to first-party chunks (same-origin, under `/_next/static/`):
 * a third-party script (analytics CDNs, ad tech, ...) can throw an error whose message
 * happens to match the patterns below, and a reload can't fix a third-party failure
 * anyway — see `isFirstPartyChunkUrl`.
 *
 * Note: we intentionally do NOT treat the App Router "Failed to fetch RSC payload … Falling
 * back to browser navigation" case as a trigger — Next already performs a hard browser
 * navigation there, so reloading on top of it would be redundant.
 */

/** sessionStorage key holding the `BUILD_ID` we last triggered a chunk-error reload on. */
export const CHUNK_RELOAD_SENTINEL_KEY = 'calm.chunkLoadReloadBuildId';

/** Next.js's own message when one of its OWN page chunks fails to load — never third-party. */
const NEXT_PAGE_CHUNK_FAILURE_PATTERN = /failed to load chunks? for page/i;

const CHUNK_LOAD_ERROR_PATTERNS: RegExp[] = [
	/ChunkLoadError/i,
	/Loading( CSS)? chunk [^\s]+ failed/i,
	NEXT_PAGE_CHUNK_FAILURE_PATTERN,
	/(failed to fetch|error loading) dynamically imported module/i,
	/importing a module script failed/i,
];

/** Read `.message` off an Error-shaped value, or the string itself; `''` for anything else. */
function reasonMessage(reason: unknown): string {
	if (typeof reason === 'string') return reason;
	const message = (reason as { message?: unknown } | null)?.message;
	return typeof message === 'string' ? message : '';
}

/**
 * Pull the failing script/module URL out of a chunk-load error, if one is present. Webpack's
 * own `ChunkLoadError` carries the request on `.request` (an absolute URL or a `/_next/...`
 * root-relative path); other engines only put it in the message text (e.g. Chrome's dynamic
 * `import()` failure, or the `(missing: <url>)` suffix webpack appends) — so fall back to
 * scanning the message for either shape. Returns `undefined` when no URL can be found at all
 * (see the fail-closed note on `isFirstPartyChunkUrl`'s caller for why that matters).
 */
function extractFailingUrl(reason: unknown): string | undefined {
	const request =
		reason && typeof reason === 'object' ? (reason as { request?: unknown }).request : undefined;
	if (typeof request === 'string' && request) return request;

	const match = reasonMessage(reason).match(/(https?:\/\/\S+|\/_next\/\S+)/i);
	// Trim trailing prose punctuation a regex match can pick up, e.g. "...js)." or "...js.".
	return match ? match[0].replace(/[).,]+$/, '') : undefined;
}

/** True if `url` (absolute, or root-relative and resolved against the current origin) points
 * at our own build's static assets rather than a third-party host. */
function isFirstPartyChunkUrl(url: string): boolean {
	try {
		const currentHref = window.location.href;
		const parsed = new URL(url, currentHref);
		return parsed.origin === new URL(currentHref).origin && parsed.pathname.startsWith('/_next/static/');
	} catch {
		return false;
	}
}

/**
 * True if `reason` looks like a first-party chunk-load / dynamic-import failure. Accepts an
 * Error, an event's `reason`/`message`, or a raw string (e.g. an `ErrorEvent.message`).
 */
export function isChunkLoadError(reason: unknown): boolean {
	if (!reason) return false;

	const nameIsChunkLoadError =
		typeof reason === 'object' && (reason as { name?: unknown }).name === 'ChunkLoadError';
	const message = reasonMessage(reason);
	if (!nameIsChunkLoadError && !CHUNK_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(message))) {
		return false;
	}

	if (NEXT_PAGE_CHUNK_FAILURE_PATTERN.test(message)) return true;

	const failingUrl = extractFailingUrl(reason);
	// Fail closed when the failing URL can't be determined at all, rather than risk reloading
	// on a third-party failure we can't rule out. The tradeoff: a first-party error whose engine
	// omits the URL (older Safari's "Importing a module script failed.", with no URL attached)
	// won't self-heal either — accepted because most engines/patterns above DO carry a URL, and
	// erring toward "don't reload on ambiguous input" matches this handler's actual incident
	// history (spurious reloads on third-party noise), not a missed first-party recovery.
	return failingUrl !== undefined && isFirstPartyChunkUrl(failingUrl);
}

/** Per-deploy build identifier, inlined client-side by `next.config.ts` (`env: { BUILD_ID }`). */
function currentBuildId(): string {
	return process.env.BUILD_ID || 'unknown';
}

/** Coerce an arbitrary rejection reason into an Error, preserving its message where present. */
function toError(reason: unknown): Error {
	if (reason instanceof Error) return reason;
	if (typeof reason === 'string') return new Error(reason);
	const message = (reason as { message?: unknown } | null)?.message;
	return new Error(typeof message === 'string' ? message : 'Unknown chunk-load error');
}

/** Query params carrying a storage-independent reload counter and the timestamp of the first
 * reload in the current window. Set via `history.replaceState` before each reload, so they ride
 * along inside the URL `location.reload()` re-fetches — this is what makes them survive storage
 * being unavailable and build ids churning between reloads, neither of which touch the URL. */
const RELOAD_COUNT_PARAM = '_ccr';
const RELOAD_WINDOW_START_PARAM = '_ccrts';

/** Hard ceiling on total reloads this handler will trigger within one reload window, enforced
 * regardless of whether the sessionStorage sentinel or BUILD_ID identity can be trusted. The
 * sentinel still catches the common case in one reload, so this rarely engages — it exists for
 * the two cases proven to defeat the sentinel: storage denied, and a client bouncing between
 * builds mid-rollout. */
const MAX_CHUNK_RELOADS = 2;

/** How long the reload counter is remembered since the first reload in its window, before a
 * later error is treated as a new incident with a fresh budget. Deliberately incident-scale
 * (minutes), not a quick cosmetic delay: the two conditions this cap targets are both
 * *persistent* (storage stays denied, a rollout churns for its whole duration), so the next
 * error in a genuine tight-loop can plausibly arrive tens of seconds later, not instantly — a
 * short window would let an ordinary gap between errors quietly reset the cap it exists to
 * enforce. This also bounds how long the params can appear in RUM/analytics view URLs. */
export const RELOAD_COUNT_WINDOW_MS = 2 * 60 * 1000;

function currentReloadWindow(): { count: number; windowStart: number } {
	try {
		const params = new URL(window.location.href).searchParams;
		const count = parseInt(params.get(RELOAD_COUNT_PARAM) ?? '', 10);
		const windowStart = parseInt(params.get(RELOAD_WINDOW_START_PARAM) ?? '', 10);
		if (!Number.isInteger(count) || count < 0 || !Number.isInteger(windowStart) || windowStart <= 0) {
			return { count: 0, windowStart: 0 };
		}
		// A window older than RELOAD_COUNT_WINDOW_MS is a stale leftover from a past incident —
		// treat it as over and grant a fresh budget rather than capping forever.
		if (Date.now() - windowStart > RELOAD_COUNT_WINDOW_MS) return { count: 0, windowStart: 0 };
		return { count, windowStart };
	} catch {
		return { count: 0, windowStart: 0 };
	}
}

/** Write the bumped count, preserving the window's start time (or opening a new window if there
 * wasn't a live one — see `currentReloadWindow`). */
function setReloadWindow(count: number, windowStart: number): void {
	try {
		const url = new URL(window.location.href);
		url.searchParams.set(RELOAD_COUNT_PARAM, String(count));
		url.searchParams.set(RELOAD_WINDOW_START_PARAM, String(windowStart));
		window.history.replaceState(window.history.state, '', url.toString());
	} catch {
		// If the URL can't be read/written, the reload still proceeds — the count resets to 0 on
		// the next load, which only weakens the belt-and-suspenders cap, not the primary guard.
	}
}

/** Strip the reload-window params once they've actually expired, so they don't linger in
 * RUM/analytics view URLs indefinitely. Scheduled for exactly when *this* window (read at
 * install time) will hit `RELOAD_COUNT_WINDOW_MS`, not a fixed short delay — stripping any
 * earlier would erase the cap's memory while the window is still legitimately live. Returns
 * the timer handle (or `undefined` if nothing was scheduled) so the caller can cancel it on
 * uninstall — otherwise it outlives a short-lived install (tests, hot reload) and can mutate
 * the URL long after nothing is listening for chunk errors. */
function scheduleReloadCountCleanup(): ReturnType<typeof setTimeout> | undefined {
	let windowStart: number;
	try {
		const raw = new URL(window.location.href).searchParams.get(RELOAD_WINDOW_START_PARAM);
		windowStart = raw === null ? NaN : parseInt(raw, 10);
	} catch {
		return undefined;
	}
	if (!Number.isInteger(windowStart) || windowStart <= 0) return undefined; // nothing to clean up

	const remaining = Math.max(RELOAD_COUNT_WINDOW_MS - (Date.now() - windowStart), 0);
	return setTimeout(() => {
		try {
			const url = new URL(window.location.href);
			// Only strip if this is still the window we scheduled for — a newer error since
			// install would have written a fresher timestamp we must not clobber.
			if (url.searchParams.get(RELOAD_WINDOW_START_PARAM) !== String(windowStart)) return;
			url.searchParams.delete(RELOAD_COUNT_PARAM);
			url.searchParams.delete(RELOAD_WINDOW_START_PARAM);
			window.history.replaceState(window.history.state, '', url.toString());
		} catch {
			// Non-fatal — a leftover param only affects reload-cap counting, not user-facing behavior.
		}
	}, remaining);
}

// Set once we've committed to reloading, or once the sentinel says this build already had its
// one reload — both are decisions that hold for the rest of this page-load's lifetime (a
// build's identity, and whether we already acted on it, don't change mid-load). A stale chunk
// often rejects several pending imports in the same tick (a route prefetch + a visible dynamic
// component); without this flag each sibling would redo the decision and duplicate the reload
// or the error log. A fresh page load starts a new module instance, so this resets on reload.
//
// The reload *cap* is deliberately NOT latched here: it's tied to the rolling window in
// `currentReloadWindow`, which can expire while this same page instance is still open (the cap
// only fires without a reload, so nothing forces a fresh page load to re-evaluate it). Latching
// it would turn a temporary, incident-scoped cap into a permanent ban on this page instance even
// after the window — and thus the incident it was guarding against — has passed.
let reloadInFlight = false;

/** Dedupes "reload cap reached" log spam within a single rolling window — set to that window's
 * `windowStart` the first time it's logged, so same-tick bursts and later errors in the SAME
 * window don't each log again, while a genuinely new window (a fresh incident, once the old one
 * expires) logs once more. */
let lastCapLoggedWindowStart = 0;

/**
 * Handle a detected chunk-load failure: reload once (per build) to pull fresh assets, unless
 * we already reloaded on this build (the deploy itself is broken) or hit the absolute reload
 * cap (build identity can't be trusted) — in which case we log and stop rather than loop.
 */
export function handleChunkLoadError(reason: unknown): void {
	if (reloadInFlight) return;

	const buildId = currentBuildId();
	const error = toError(reason);
	const { count: reloadCount, windowStart } = currentReloadWindow();

	if (reloadCount >= MAX_CHUNK_RELOADS) {
		if (windowStart !== lastCapLoggedWindowStart) {
			lastCapLoggedWindowStart = windowStart;
			calmLogger.error(
				'ChunkLoadError reload cap reached; not reloading again to avoid a loop',
				{ buildId, reloadCount },
				error,
			);
		}
		return;
	}

	let alreadyReloadedThisBuild = false;
	try {
		alreadyReloadedThisBuild = window.sessionStorage.getItem(CHUNK_RELOAD_SENTINEL_KEY) === buildId;
	} catch {
		// sessionStorage can throw (privacy mode / storage disabled). Treat as "not yet
		// reloaded" so the common stale-chunk case still self-heals once.
	}

	if (alreadyReloadedThisBuild) {
		reloadInFlight = true;
		calmLogger.error(
			'ChunkLoadError persisted after reload on the same build; not reloading again to avoid a loop',
			{ buildId },
			error,
		);
		return;
	}

	reloadInFlight = true;
	try {
		window.sessionStorage.setItem(CHUNK_RELOAD_SENTINEL_KEY, buildId);
	} catch {
		// Ignore — without storage we can't dedupe across the reload, but a single reload
		// is still the right move for a stale chunk. The `_ccr` cap above is the backstop.
	}
	setReloadWindow(reloadCount + 1, windowStart || Date.now());

	calmLogger.warn('ChunkLoadError detected; reloading once to fetch fresh assets', {
		buildId,
		reloadCount: reloadCount + 1,
		message: error.message,
	});
	window.location.reload();
}

let installed = false;

/**
 * Install global listeners that recover from post-deploy stale chunks. Idempotent and a
 * no-op on the server. Call once from `instrumentation-client.ts` so it covers both the
 * App Router and the Pages Router. Returns a cleanup function that removes the listeners.
 */
export function installChunkLoadErrorReloadHandler(): () => void {
	if (typeof window === 'undefined' || installed) {
		return () => {};
	}
	installed = true;

	const cleanupTimer = scheduleReloadCountCleanup();

	// Dynamic imports (`next/dynamic`, `import()`) surface as rejected promises.
	const onUnhandledRejection = (event: PromiseRejectionEvent) => {
		if (isChunkLoadError(event.reason)) handleChunkLoadError(event.reason);
	};
	// Thrown ChunkLoadErrors (e.g. during render / route transitions) surface here.
	const onError = (event: ErrorEvent) => {
		const candidate = event.error ?? event.message;
		if (isChunkLoadError(candidate)) handleChunkLoadError(candidate);
	};

	window.addEventListener('unhandledrejection', onUnhandledRejection);
	window.addEventListener('error', onError);

	return () => {
		installed = false;
		if (cleanupTimer !== undefined) clearTimeout(cleanupTimer);
		window.removeEventListener('unhandledrejection', onUnhandledRejection);
		window.removeEventListener('error', onError);
	};
}
